import { formatHtml } from "./htmlFormatter";
import { convertTimestampInput } from "./timestampUtils";
import type {
  OutputKind,
  ToolDefinition,
  ToolId,
  ToolModeId,
  TransformErrorCode,
  UrlEncodeModeId
} from "./types";

const encoder = new TextEncoder();
const decoder = new TextDecoder("utf-8", { fatal: true });
const validJsonStringEscapes = new Set(['"', "\\", "/", "b", "f", "n", "r", "t", "u"]);
const htmlEntityEntries = [
  ["amp", "&"],
  ["lt", "<"],
  ["gt", ">"],
  ["quot", '"'],
  ["apos", "'"],
  ["nbsp", "\u00a0"]
] as const;
const htmlEntityLookup = new Map<string, string>(htmlEntityEntries);
const cronFieldSpecs = {
  minute: { min: 0, max: 59, label: "minute" },
  hour: { min: 0, max: 23, label: "hour" },
  dayOfMonth: { min: 1, max: 31, label: "day of month" },
  month: { min: 1, max: 12, label: "month" },
  dayOfWeek: { min: 0, max: 7, label: "day of week" }
} as const;

interface CronFieldDescription {
  expression: string;
  description: string;
}

interface CronDescription {
  normalizedExpression: string;
  summary: string;
  fields: Record<
    "minute" | "hour" | "dayOfMonth" | "month" | "dayOfWeek",
    CronFieldDescription
  >;
}

interface ParsedCurlCommand {
  url: string;
  method: string;
  headers: Record<string, string>;
  data: string | null;
  followRedirects: boolean;
}

interface TextDiffPayload {
  left: string;
  right: string;
}

interface TextDiffRow {
  type: "unchanged" | "added" | "removed";
  leftLine: number | null;
  rightLine: number | null;
  text: string;
}

interface TextDiffResult {
  summary: {
    added: number;
    removed: number;
    unchanged: number;
  };
  rows: TextDiffRow[];
}

export class TransformError extends Error {
  constructor(public readonly code: TransformErrorCode) {
    super(code);
    this.name = "TransformError";
  }
}

export const toolDefinitions: ToolDefinition[] = [
  {
    id: "json",
    defaultModeId: "json-format",
    layoutKind: "split",
    modeIds: [
      "json-format",
      "json-minify",
      "json-escape",
      "json-unescape"
    ]
  },
  {
    id: "url-encode",
    defaultModeId: "url-safe",
    layoutKind: "split",
    modeIds: ["url-safe", "url-full", "url-decode"]
  },
  {
    id: "text-codec",
    defaultModeId: "text-encode",
    layoutKind: "split",
    modeIds: ["text-encode", "text-decode"]
  },
  {
    id: "base64",
    defaultModeId: "base64-encode",
    layoutKind: "split",
    modeIds: ["base64-encode", "base64-decode", "base64-image"]
  },
  {
    id: "html",
    defaultModeId: "html-format",
    layoutKind: "split",
    modeIds: ["html-format", "html-preview", "entity-encode", "entity-decode"]
  },
  {
    id: "timestamp",
    defaultModeId: "timestamp-convert",
    layoutKind: "timestamp",
    modeIds: ["timestamp-convert"]
  },
  {
    id: "cron",
    defaultModeId: "cron-describe",
    layoutKind: "cron",
    modeIds: ["cron-describe"]
  },
  {
    id: "curl",
    defaultModeId: "curl-to-python",
    layoutKind: "stack",
    modeIds: [
      "curl-to-python",
      "curl-to-fetch",
      "curl-to-axios",
      "curl-to-node",
      "curl-to-go",
      "curl-to-php"
    ]
  },
  {
    id: "text-diff",
    defaultModeId: "text-diff-compare",
    layoutKind: "diff",
    modeIds: ["text-diff-compare"]
  },
  {
    id: "query-string",
    defaultModeId: "query-parse",
    layoutKind: "split",
    modeIds: ["query-parse", "query-build"]
  },
  {
    id: "cookie",
    defaultModeId: "cookie-parse",
    layoutKind: "split",
    modeIds: ["cookie-parse", "set-cookie-parse"]
  }
];

export const defaultToolId: ToolId = "json";

export const modeOutputKinds: Record<ToolModeId, OutputKind> = {
  "json-format": "json",
  "json-minify": "text",
  "json-escape": "text",
  "json-unescape": "text",
  "url-safe": "text",
  "url-full": "text",
  "url-decode": "text",
  "text-encode": "text",
  "text-decode": "text",
  "base64-encode": "text",
  "base64-decode": "text",
  "base64-image": "image-preview",
  "html-format": "text",
  "html-preview": "html-preview",
  "entity-encode": "text",
  "entity-decode": "text",
  "timestamp-convert": "timestamp-result",
  "cron-describe": "json",
  "curl-to-fetch": "text",
  "curl-to-axios": "text",
  "curl-to-python": "text",
  "curl-to-node": "text",
  "curl-to-go": "text",
  "curl-to-php": "text",
  "text-diff-compare": "text-diff-result",
  "query-parse": "json",
  "query-build": "text",
  "cookie-parse": "json",
  "set-cookie-parse": "json"
};

export const sampleInputs: Record<ToolModeId, string> = {
  "json-format":
    '{"task":"crawl-debug","retry":2,"targets":["https://api.example.com/search?q=proxy","https://api.example.com/detail?id=8848"],"headers":{"x-trace":"crawler-batch-17","user-agent":"Mozilla/5.0"},"schedule":{"enabled":true,"window":"00:00-06:00"}}',
  "json-minify":
    '{\n  "task": "crawl-debug",\n  "retry": 2,\n  "headers": {\n    "x-trace": "crawler-batch-17",\n    "user-agent": "Mozilla/5.0"\n  },\n  "schedule": {\n    "enabled": true,\n    "window": "00:00-06:00"\n  }\n}',
  "json-escape":
    '{"task":"crawl-debug","message":"cookie expired\\nneed manual refresh","ok":false}',
  "json-unescape":
    '{\\"task\\":\\"crawl-debug\\",\\"message\\":\\"cookie expired\\\\nneed manual refresh\\",\\"ok\\":false}',
  "url-safe":
    "https://example.com/search?q=代理池 调试&redirect=https://foo.bar/callback?a=1&b=2",
  "url-full":
    "crawler debug/代理池?step=1&status=ready",
  "url-decode":
    "https%3A%2F%2Fexample.com%2Fsearch%3Fq%3D%E4%BB%A3%E7%90%86%E6%B1%A0%2520%E8%B0%83%E8%AF%95%26step%3D1",
  "text-encode":
    "代理池调试\ncookie expired, retry in 5 minutes.",
  "text-decode":
    "\\u4EE3\\u7406\\u6C60\\u8C03\\u8BD5\\ncookie expired, retry in 5 minutes.",
  "base64-encode":
    "代理池调试\ncookie expired, retry in 5 minutes.",
  "base64-decode":
    "5Luj55CG5rGg6LCD6K+VCmNvb2tpZSBleHBpcmVkLCByZXRyeSBpbiA1IG1pbnV0ZXMu",
  "base64-image":
    "iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/x8AAwMCAO+nX4QAAAAASUVORK5CYII=",
  "html-format":
    '<!doctype html><html><body><main class="result-card"><h1>justtool</h1><p>Focus on fast, local text transforms for crawler workflows.</p></main></body></html>',
  "html-preview":
    '<section style="font-family:system-ui,sans-serif;padding:24px;border-radius:16px;background:linear-gradient(135deg,#f0fdf4,#ecfeff);color:#134e4a"><h1 style="margin:0 0 12px">HTML Preview</h1><p style="margin:0">Render your snippet instantly in a sandboxed preview pane.</p></section>',
  "entity-encode":
    `<div class="notice">Tom & "Jerry"</div>`,
  "entity-decode":
    "&lt;div class=&quot;notice&quot;&gt;Tom &amp; &#39;Jerry&#39;&lt;/div&gt;",
  "timestamp-convert":
    "1711497600",
  "cron-describe":
    "*/15 9-18 * * 1-5",
  "curl-to-fetch":
    "curl 'https://api.example.com/search?q=proxy' -X POST -H 'content-type: application/json' -H 'x-trace: crawler-batch-17' --data-raw '{\"page\":1,\"keyword\":\"proxy pool\"}'",
  "curl-to-axios":
    "curl 'https://api.example.com/search?q=proxy' -X POST -H 'content-type: application/json' -H 'x-trace: crawler-batch-17' --data-raw '{\"page\":1,\"keyword\":\"proxy pool\"}'",
  "curl-to-python":
    "curl 'https://api.example.com/search?q=proxy' -X POST -H 'content-type: application/json' -H 'x-trace: crawler-batch-17' --data-raw '{\"page\":1,\"keyword\":\"proxy pool\"}'",
  "curl-to-node":
    "curl 'https://api.example.com/search?q=proxy' -X POST -H 'content-type: application/json' -H 'x-trace: crawler-batch-17' --data-raw '{\"page\":1,\"keyword\":\"proxy pool\"}'",
  "curl-to-go":
    "curl 'https://api.example.com/search?q=proxy' -X POST -H 'content-type: application/json' -H 'x-trace: crawler-batch-17' --data-raw '{\"page\":1,\"keyword\":\"proxy pool\"}'",
  "curl-to-php":
    "curl 'https://api.example.com/search?q=proxy' -X POST -H 'content-type: application/json' -H 'x-trace: crawler-batch-17' --data-raw '{\"page\":1,\"keyword\":\"proxy pool\"}'",
  "text-diff-compare":
    "line 1\nline 2\nline 3",
  "query-parse":
    "q=%E4%BB%A3%E7%90%86%E6%B1%A0+debug&tag=proxy&tag=pool&empty=",
  "query-build":
    "q=代理池 debug\ntag=proxy\ntag=pool\nempty=",
  "cookie-parse":
    "session=abc123; theme=dark; csrftoken=retry-token",
  "set-cookie-parse":
    "session=abc123; Path=/; HttpOnly; Secure; SameSite=Lax\npref=dark; Expires=Wed, 21 Oct 2026 07:28:00 GMT; Domain=.example.com"
};

export function runMode(modeId: ToolModeId, input: string): string {
  switch (modeId) {
    case "json-format":
      return JSON.stringify(parseJson(input), null, 2);
    case "json-minify":
      return JSON.stringify(parseJson(input));
    case "json-escape":
      return JSON.stringify(input).slice(1, -1);
    case "json-unescape":
      return unescapeJsonFragment(input);
    case "url-safe":
      return encodeURI(input);
    case "url-full":
      return encodeURIComponent(input);
    case "url-decode":
      return decodeUrl(input);
    case "text-encode":
      return encodeText(input);
    case "text-decode":
      return decodeText(input);
    case "base64-encode":
      return encodeBase64(input);
    case "base64-decode":
      return decodeBase64(input);
    case "base64-image":
      return decodeBase64Image(input);
    case "html-format":
      return formatHtml(input);
    case "html-preview":
      return input;
    case "entity-encode":
      return encodeHtmlEntities(input);
    case "entity-decode":
      return decodeHtmlEntities(input);
    case "timestamp-convert":
      return JSON.stringify(convertTimestamp(input), null, 2);
    case "cron-describe":
      return JSON.stringify(describeCronExpression(input), null, 2);
    case "curl-to-fetch":
      return renderCurlAsFetch(parseCurlCommand(input));
    case "curl-to-axios":
      return renderCurlAsAxios(parseCurlCommand(input));
    case "curl-to-python":
      return renderCurlAsPythonRequests(parseCurlCommand(input));
    case "curl-to-node":
      return renderCurlAsNode(parseCurlCommand(input));
    case "curl-to-go":
      return renderCurlAsGo(parseCurlCommand(input));
    case "curl-to-php":
      return renderCurlAsPhp(parseCurlCommand(input));
    case "text-diff-compare":
      return JSON.stringify(compareTextDiff(input), null, 2);
    case "query-parse":
      return JSON.stringify(parseQueryString(input), null, 2);
    case "query-build":
      return buildQueryString(input);
    case "cookie-parse":
      return JSON.stringify(parseCookieHeader(input), null, 2);
    case "set-cookie-parse":
      return JSON.stringify(parseSetCookieHeaders(input), null, 2);
  }
}

export function countBytes(value: string): number {
  return encoder.encode(value).byteLength;
}

export function getToolDefinition(toolId: ToolId): ToolDefinition {
  const definition = toolDefinitions.find((tool) => tool.id === toolId);

  if (!definition) {
    throw new Error(`Unknown tool: ${toolId}`);
  }

  return definition;
}

export function isToolId(value: string | null): value is ToolId {
  return value !== null && toolDefinitions.some((tool) => tool.id === value);
}

export function isModeId(value: string | null): value is ToolModeId {
  return value !== null && Object.hasOwn(sampleInputs, value);
}

export function isUrlEncodeModeId(modeId: ToolModeId): modeId is UrlEncodeModeId {
  return modeId === "url-safe" || modeId === "url-full";
}

export function getModeOutputKind(modeId: ToolModeId): OutputKind {
  return modeOutputKinds[modeId];
}

function parseJson(input: string): unknown {
  try {
    return JSON.parse(input);
  } catch {
    try {
      return JSON.parse(repairInvalidJsonStringEscapes(input));
    } catch {
      throw new TransformError("INVALID_JSON");
    }
  }
}

function repairInvalidJsonStringEscapes(input: string): string {
  let inString = false;
  let lastCopyIndex = 0;
  let repaired = "";

  for (let index = 0; index < input.length; index += 1) {
    const current = input[index];

    if (!inString) {
      if (current === '"') {
        inString = true;
      }

      continue;
    }

    if (current === "\\") {
      const next = input[index + 1];

      if (next === undefined) {
        continue;
      }

      if (!validJsonStringEscapes.has(next)) {
        repaired += input.slice(lastCopyIndex, index) + next;
        lastCopyIndex = index + 2;
      }

      index += 1;
      continue;
    }

    if (current === '"') {
      inString = false;
    }
  }

  return repaired === "" ? input : repaired + input.slice(lastCopyIndex);
}

function unescapeJsonFragment(input: string): string {
  const trimmed = input.trim();

  if (trimmed === "") {
    return "";
  }

  const candidates = new Set<string>();

  if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
    candidates.add(trimmed);
  }

  candidates.add(`"${trimmed}"`);

  for (const candidate of candidates) {
    try {
      const parsed = JSON.parse(candidate);

      if (typeof parsed === "string") {
        return parsed;
      }
    } catch {
      continue;
    }
  }

  throw new TransformError("INVALID_ESCAPED_JSON");
}

function decodeUrl(input: string): string {
  const trimmed = input.trim();

  if (trimmed === "") {
    return "";
  }

  try {
    return decodeURIComponent(input);
  } catch {
    try {
      return decodeURI(input);
    } catch {
      throw new TransformError("INVALID_URL_ENCODED_TEXT");
    }
  }
}

function parseQueryString(input: string): Array<{ key: string; value: string }> {
  const normalized = normalizeQuerySource(input);

  if (normalized === "") {
    return [];
  }

  return normalized
    .split("&")
    .filter((segment) => segment !== "")
    .map((segment) => {
      const separatorIndex = segment.indexOf("=");
      const key = separatorIndex >= 0 ? segment.slice(0, separatorIndex) : segment;
      const value = separatorIndex >= 0 ? segment.slice(separatorIndex + 1) : "";

      return {
        key: decodeQueryComponent(key),
        value: decodeQueryComponent(value)
      };
    });
}

function buildQueryString(input: string): string {
  return input
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line !== "")
    .map((line) => {
      const separatorIndex = line.indexOf("=");
      const key = separatorIndex >= 0 ? line.slice(0, separatorIndex) : line;
      const value = separatorIndex >= 0 ? line.slice(separatorIndex + 1) : "";

      return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
    })
    .join("&");
}

function parseCookieHeader(input: string): Array<{ name: string; value: string }> {
  const normalized = input.trim();

  if (normalized === "") {
    return [];
  }

  return normalized
    .split(";")
    .map((segment) => segment.trim())
    .filter((segment) => segment !== "")
    .map((segment) => parseNameValuePair(segment, "="));
}

function parseSetCookieHeaders(input: string): Array<{
  name: string;
  value: string;
  attributes: Record<string, string | true>;
}> {
  return input
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line !== "")
    .map((line) => parseSetCookieLine(line));
}

function convertTimestamp(input: string): {
  detectedType: "seconds" | "milliseconds" | "datetime";
  seconds: string;
  milliseconds: string;
  isoUtc: string;
} {
  try {
    const result = convertTimestampInput(input);

    return {
      detectedType: result.detectedType,
      seconds: result.seconds,
      milliseconds: result.milliseconds,
      isoUtc: result.isoUtc
    };
  } catch {
    throw new TransformError("INVALID_TIMESTAMP");
  }
}

function describeCronExpression(input: string): CronDescription {
  const normalized = normalizeCronAlias(input.trim());

  if (normalized === "") {
    throw new TransformError("INVALID_CRON_EXPRESSION");
  }

  const segments = normalized.split(/\s+/);

  if (segments.length !== 5) {
    throw new TransformError("INVALID_CRON_EXPRESSION");
  }

  const [minute, hour, dayOfMonth, month, dayOfWeek] = segments;

  return {
    normalizedExpression: normalized,
    summary: buildCronSummary(minute, hour, dayOfMonth, month, dayOfWeek),
    fields: {
      minute: describeCronField(minute, cronFieldSpecs.minute),
      hour: describeCronField(hour, cronFieldSpecs.hour),
      dayOfMonth: describeCronField(dayOfMonth, cronFieldSpecs.dayOfMonth),
      month: describeCronField(month, cronFieldSpecs.month),
      dayOfWeek: describeCronField(dayOfWeek, cronFieldSpecs.dayOfWeek)
    }
  };
}

function parseCurlCommand(input: string): ParsedCurlCommand {
  const normalized = input.trim();

  if (normalized === "") {
    throw new TransformError("INVALID_CURL_COMMAND");
  }

  const tokens = tokenizeShellCommand(normalized);

  if (tokens.length === 0 || tokens[0] !== "curl") {
    throw new TransformError("INVALID_CURL_COMMAND");
  }

  let url = "";
  let method = "";
  const headers: Record<string, string> = {};
  const dataParts: string[] = [];
  let followRedirects = false;

  for (let index = 1; index < tokens.length; index += 1) {
    const token = tokens[index];

    switch (token) {
      case "-X":
      case "--request":
        method = tokens[index + 1] ?? "";
        index += 1;
        break;
      case "-H":
      case "--header": {
        const headerValue = tokens[index + 1] ?? "";
        index += 1;

        if (headerValue === "") {
          throw new TransformError("INVALID_CURL_COMMAND");
        }

        const parsedHeader = parseNameValuePair(headerValue, ":");
        headers[parsedHeader.name] = parsedHeader.value;
        break;
      }
      case "-d":
      case "--data":
      case "--data-raw":
      case "--data-binary":
      case "--data-ascii":
        dataParts.push(tokens[index + 1] ?? "");
        index += 1;
        break;
      case "-L":
      case "--location":
        followRedirects = true;
        break;
      case "--url":
        url = tokens[index + 1] ?? "";
        index += 1;
        break;
      default:
        if (token.startsWith("-")) {
          continue;
        }

        if (url === "") {
          url = token;
        }
        break;
    }
  }

  if (url === "") {
    throw new TransformError("INVALID_CURL_COMMAND");
  }

  return {
    url,
    method: (method || (dataParts.length > 0 ? "POST" : "GET")).toUpperCase(),
    headers,
    data: dataParts.length > 0 ? dataParts.join("&") : null,
    followRedirects
  };
}

function renderCurlAsFetch(command: ParsedCurlCommand): string {
  const lines = [`const url = ${JSON.stringify(command.url)};`, ""];
  const headersBlock = renderJsHeaders(command.headers);

  if (headersBlock) {
    lines.push(headersBlock, "");
  }

  if (command.data !== null) {
    lines.push(renderFetchBody(command), "");
  }

  lines.push("const response = await fetch(url, {");
  lines.push(`  method: ${JSON.stringify(command.method)},`);

  if (headersBlock) {
    lines.push("  headers,");
  }

  if (command.data !== null) {
    lines.push("  body,");
  }

  if (command.followRedirects) {
    lines.push('  redirect: "follow",');
  }

  lines.push("});", "", "const result = await response.text();", "console.log(result);");
  return lines.join("\n");
}

function renderCurlAsAxios(command: ParsedCurlCommand): string {
  const lines = ['import axios from "axios";', "", "const response = await axios({", `  method: ${JSON.stringify(command.method.toLowerCase())},`, `  url: ${JSON.stringify(command.url)},`];
  const headersObject = renderJsObject(command.headers, 2);

  if (headersObject) {
    lines.push("  headers: " + headersObject + ",");
  }

  if (command.data !== null) {
    lines.push(`  data: ${renderJsBodyValue(command)},`);
  }

  lines.push("});", "", "console.log(response.data);");
  return lines.join("\n");
}

function renderCurlAsPythonRequests(command: ParsedCurlCommand): string {
  const lines = ["import requests", "", `url = ${JSON.stringify(command.url)}`];

  if (Object.keys(command.headers).length > 0) {
    lines.push(`headers = ${renderPythonDict(command.headers)}`);
  }

  if (command.data !== null) {
    lines.push(`${isJsonBody(command) ? "json_payload" : "data"} = ${renderPythonBodyValue(command)}`);
  }

  lines.push("");
  const requestArgs = [`${JSON.stringify(command.method)}`, "url"];

  if (Object.keys(command.headers).length > 0) {
    requestArgs.push("headers=headers");
  }

  if (command.data !== null) {
    requestArgs.push(isJsonBody(command) ? "json=json_payload" : "data=data");
  }

  if (command.followRedirects) {
    requestArgs.push("allow_redirects=True");
  }

  lines.push(`response = requests.request(${requestArgs.join(", ")})`, "print(response.text)");
  return lines.join("\n");
}

function renderCurlAsNode(command: ParsedCurlCommand): string {
  const lines = [
    'import http from "node:http";',
    'import https from "node:https";',
    "",
    `const url = new URL(${JSON.stringify(command.url)});`,
    'const client = url.protocol === "https:" ? https : http;'
  ];
  const headersObject = renderJsObject(command.headers, 1);

  lines.push("", "const options = {", `  method: ${JSON.stringify(command.method)},`);

  if (headersObject) {
    lines.push(`  headers: ${headersObject},`);
  }

  lines.push("};");

  if (command.data !== null) {
    lines.push("", `const body = ${renderNodeBodyValue(command)};`);
  }

  lines.push(
    "",
    "const req = client.request(url, options, (res) => {",
    '  let data = "";',
    '  res.setEncoding("utf8");',
    '  res.on("data", (chunk) => {',
    "    data += chunk;",
    "  });",
    '  res.on("end", () => {',
    "    console.log(data);",
    "  });",
    "});",
    "",
    'req.on("error", (error) => {',
    "  console.error(error);",
    "});"
  );

  if (command.data !== null) {
    lines.push("", "req.write(body);");
  }

  lines.push("", "req.end();");
  return lines.join("\n");
}

function renderCurlAsGo(command: ParsedCurlCommand): string {
  const imports = ['  "fmt"', '  "io"', '  "net/http"'];

  if (command.data !== null) {
    imports.unshift('  "strings"');
  }

  const lines = ["package main", "", "import (", ...imports, ")", "", "func main() {"];
  const bodyExpression =
    command.data !== null ? `strings.NewReader(${JSON.stringify(command.data)})` : "nil";

  lines.push(
    `  req, err := http.NewRequest(${JSON.stringify(command.method)}, ${JSON.stringify(command.url)}, ${bodyExpression})`,
    "  if err != nil {",
    "    panic(err)",
    "  }"
  );

  for (const [key, value] of Object.entries(command.headers)) {
    lines.push(`  req.Header.Set(${JSON.stringify(key)}, ${JSON.stringify(value)})`);
  }

  lines.push(
    "",
    "  client := &http.Client{}",
    "  resp, err := client.Do(req)",
    "  if err != nil {",
    "    panic(err)",
    "  }",
    "  defer resp.Body.Close()",
    "",
    "  body, err := io.ReadAll(resp.Body)",
    "  if err != nil {",
    "    panic(err)",
    "  }",
    "",
    "  fmt.Println(string(body))",
    "}"
  );

  return lines.join("\n");
}

function renderCurlAsPhp(command: ParsedCurlCommand): string {
  const lines = ["<?php", "", "$ch = curl_init();", "", "curl_setopt_array($ch, ["];
  const headerLines = Object.entries(command.headers).map(
    ([key, value]) => `        ${JSON.stringify(`${key}: ${value}`)},`
  );

  lines.push(`    CURLOPT_URL => ${JSON.stringify(command.url)},`);
  lines.push("    CURLOPT_RETURNTRANSFER => true,");
  lines.push(`    CURLOPT_CUSTOMREQUEST => ${JSON.stringify(command.method)},`);

  if (headerLines.length > 0) {
    lines.push("    CURLOPT_HTTPHEADER => [", ...headerLines, "    ],");
  }

  if (command.data !== null) {
    lines.push(`    CURLOPT_POSTFIELDS => ${JSON.stringify(command.data)},`);
  }

  if (command.followRedirects) {
    lines.push("    CURLOPT_FOLLOWLOCATION => true,");
  }

  lines.push(
    "]);",
    "",
    "$response = curl_exec($ch);",
    "",
    "if ($response === false) {",
    "    throw new RuntimeException(curl_error($ch));",
    "}",
    "",
    "curl_close($ch);",
    "",
    "echo $response;"
  );

  return lines.join("\n");
}

function compareTextDiff(input: string): TextDiffResult {
  let payload: TextDiffPayload;

  try {
    payload = JSON.parse(input) as TextDiffPayload;
  } catch {
    throw new TransformError("UNKNOWN");
  }

  const leftLines = payload.left.split(/\r?\n/);
  const rightLines = payload.right.split(/\r?\n/);
  const table = buildLcsTable(leftLines, rightLines);
  const rows: TextDiffRow[] = [];
  let leftIndex = 0;
  let rightIndex = 0;
  let added = 0;
  let removed = 0;
  let unchanged = 0;

  while (leftIndex < leftLines.length && rightIndex < rightLines.length) {
    if (leftLines[leftIndex] === rightLines[rightIndex]) {
      unchanged += 1;
      rows.push({
        type: "unchanged",
        leftLine: leftIndex + 1,
        rightLine: rightIndex + 1,
        text: leftLines[leftIndex]
      });
      leftIndex += 1;
      rightIndex += 1;
      continue;
    }

    if (table[leftIndex + 1][rightIndex] >= table[leftIndex][rightIndex + 1]) {
      removed += 1;
      rows.push({
        type: "removed",
        leftLine: leftIndex + 1,
        rightLine: null,
        text: leftLines[leftIndex]
      });
      leftIndex += 1;
      continue;
    }

    added += 1;
    rows.push({
      type: "added",
      leftLine: null,
      rightLine: rightIndex + 1,
      text: rightLines[rightIndex]
    });
    rightIndex += 1;
  }

  while (leftIndex < leftLines.length) {
    removed += 1;
    rows.push({
      type: "removed",
      leftLine: leftIndex + 1,
      rightLine: null,
      text: leftLines[leftIndex]
    });
    leftIndex += 1;
  }

  while (rightIndex < rightLines.length) {
    added += 1;
    rows.push({
      type: "added",
      leftLine: null,
      rightLine: rightIndex + 1,
      text: rightLines[rightIndex]
    });
    rightIndex += 1;
  }

  return {
    summary: {
      added,
      removed,
      unchanged
    },
    rows
  };
}

function extractUrls(input: string): string[] {
  const matches = input.match(/https?:\/\/[^\s"'<>`]+/gi) ?? [];
  return dedupeStrings(matches.map((match) => trimTrailingUrlPunctuation(match)));
}

function extractDomains(input: string): string[] {
  return dedupeStrings(
    extractUrls(input)
      .map((url) => {
        try {
          return new URL(url).hostname;
        } catch {
          return "";
        }
      })
      .filter((hostname) => hostname !== "")
  );
}

function parseJwt(input: string): Record<string, unknown> {
  const normalized = input.trim();

  if (normalized === "") {
    return {};
  }

  const parts = normalized.split(".");

  if (parts.length < 2 || parts.length > 3) {
    throw new TransformError("INVALID_JWT");
  }

  const header = decodeJwtPart(parts[0]);
  const payload = decodeJwtPart(parts[1]);
  const result: Record<string, unknown> = {
    header,
    payload
  };
  const registeredClaims = getJwtRegisteredClaims(payload);

  if (parts[2] !== undefined) {
    result.signature = parts[2];
  }

  if (Object.keys(registeredClaims).length > 0) {
    result.registeredClaims = registeredClaims;
  }

  return result;
}

function timestampFromSeconds(input: string): string {
  return formatTimestampInput(input, 1000);
}

function timestampFromMilliseconds(input: string): string {
  return formatTimestampInput(input, 1);
}

function timestampToSeconds(input: string): string {
  const timestamp = parseDateInput(input);
  return Math.trunc(timestamp / 1000).toString();
}

function timestampToMilliseconds(input: string): string {
  return parseDateInput(input).toString();
}

function encodeText(input: string): string {
  let output = "";

  for (let index = 0; index < input.length; index += 1) {
    const codeUnit = input.charCodeAt(index);
    const character = input[index];

    switch (character) {
      case "\\":
        output += "\\\\";
        break;
      case "\n":
        output += "\\n";
        break;
      case "\r":
        output += "\\r";
        break;
      case "\t":
        output += "\\t";
        break;
      case "\b":
        output += "\\b";
        break;
      case "\f":
        output += "\\f";
        break;
      default:
        if (codeUnit >= 0x20 && codeUnit <= 0x7e) {
          output += character;
          break;
        }

        output += `\\u${codeUnit.toString(16).toUpperCase().padStart(4, "0")}`;
        break;
    }
  }

  return output;
}

function decodeText(input: string): string {
  let output = "";

  for (let index = 0; index < input.length; index += 1) {
    const character = input[index];

    if (character !== "\\") {
      output += character;
      continue;
    }

    const nextCharacter = input[index + 1];

    if (nextCharacter === undefined) {
      throw new TransformError("INVALID_TEXT_ENCODED_TEXT");
    }

    switch (nextCharacter) {
      case "\\":
        output += "\\";
        index += 1;
        break;
      case "n":
        output += "\n";
        index += 1;
        break;
      case "r":
        output += "\r";
        index += 1;
        break;
      case "t":
        output += "\t";
        index += 1;
        break;
      case "b":
        output += "\b";
        index += 1;
        break;
      case "f":
        output += "\f";
        index += 1;
        break;
      case '"':
        output += '"';
        index += 1;
        break;
      case "'":
        output += "'";
        index += 1;
        break;
      case "u": {
        const hex = input.slice(index + 2, index + 6);

        if (!/^[0-9a-fA-F]{4}$/.test(hex)) {
          throw new TransformError("INVALID_TEXT_ENCODED_TEXT");
        }

        output += String.fromCharCode(Number.parseInt(hex, 16));
        index += 5;
        break;
      }
      default:
        throw new TransformError("INVALID_TEXT_ENCODED_TEXT");
    }
  }

  return output;
}

function encodeHex(input: string): string {
  const bytes = encoder.encode(input);
  let output = "";

  for (const byte of bytes) {
    output += byte.toString(16).padStart(2, "0");
  }

  return output;
}

function decodeHex(input: string): string {
  const normalized = input.replace(/\s+/g, "").trim();

  if (normalized === "") {
    return "";
  }

  if (normalized.length % 2 !== 0 || !/^[0-9a-fA-F]+$/.test(normalized)) {
    throw new TransformError("INVALID_HEX_TEXT");
  }

  const bytes = new Uint8Array(normalized.length / 2);

  for (let index = 0; index < normalized.length; index += 2) {
    bytes[index / 2] = Number.parseInt(normalized.slice(index, index + 2), 16);
  }

  try {
    return decoder.decode(bytes);
  } catch {
    throw new TransformError("INVALID_HEX_TEXT");
  }
}

function encodeHtmlEntities(input: string): string {
  return input
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function decodeHtmlEntities(input: string): string {
  return input.replaceAll(
    /&(#x[0-9a-fA-F]+|#\d+|[a-zA-Z][a-zA-Z0-9]+);/g,
    (entity, token: string) => decodeHtmlEntityToken(token) ?? entity
  );
}

function encodeBase64(input: string): string {
  const bytes = encoder.encode(input);

  return bytesToBase64(bytes);
}

function decodeBase64(input: string): string {
  const bytes = decodeBase64ToBytes(input);

  if (bytes.length === 0) {
    return "";
  }

  try {
    return decoder.decode(bytes);
  } catch {
    throw new TransformError("INVALID_BASE64_TEXT");
  }
}

function decodeBase64Image(input: string): string {
  const normalized = input.trim();

  if (normalized === "") {
    return "";
  }

  const dataUrlMatch = normalized.match(
    /^data:(image\/[a-zA-Z0-9.+-]+);base64,([\s\S]+)$/i
  );

  if (dataUrlMatch) {
    const mimeType = dataUrlMatch[1].toLowerCase();
    const bytes = decodeBase64ToBytes(dataUrlMatch[2], "INVALID_BASE64_IMAGE");

    if (!isSupportedImageMime(mimeType) || !isImageBytes(bytes, mimeType)) {
      throw new TransformError("INVALID_BASE64_IMAGE");
    }

    return `data:${mimeType};base64,${bytesToBase64(bytes)}`;
  }

  const bytes = decodeBase64ToBytes(normalized, "INVALID_BASE64_IMAGE");
  const mimeType = detectImageMime(bytes);

  if (!mimeType) {
    throw new TransformError("INVALID_BASE64_IMAGE");
  }

  return `data:${mimeType};base64,${bytesToBase64(bytes)}`;
}

function bytesToBase64(bytes: Uint8Array): string {
  if (bytes.length === 0) {
    return "";
  }

  let binary = "";
  const chunkSize = 0x8000;

  for (let index = 0; index < bytes.length; index += chunkSize) {
    const chunk = bytes.subarray(index, index + chunkSize);
    binary += String.fromCharCode(...chunk);
  }

  return btoa(binary);
}

function decodeBase64ToBytes(
  input: string,
  errorCode: TransformErrorCode = "INVALID_BASE64_TEXT"
): Uint8Array {
  const normalized = input.replace(/\s+/g, "");

  if (normalized === "") {
    return new Uint8Array();
  }

  if (
    normalized.length % 4 !== 0 ||
    !/^[A-Za-z0-9+/]*={0,2}$/.test(normalized)
  ) {
    throw new TransformError(errorCode);
  }

  try {
    const binary = atob(normalized);
    const bytes = new Uint8Array(binary.length);

    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }

    return bytes;
  } catch {
    throw new TransformError(errorCode);
  }
}

function detectImageMime(bytes: Uint8Array): string | null {
  if (
    bytes.length >= 8 &&
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47 &&
    bytes[4] === 0x0d &&
    bytes[5] === 0x0a &&
    bytes[6] === 0x1a &&
    bytes[7] === 0x0a
  ) {
    return "image/png";
  }

  if (
    bytes.length >= 3 &&
    bytes[0] === 0xff &&
    bytes[1] === 0xd8 &&
    bytes[2] === 0xff
  ) {
    return "image/jpeg";
  }

  if (
    bytes.length >= 6 &&
    bytes[0] === 0x47 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x38 &&
    (bytes[4] === 0x37 || bytes[4] === 0x39) &&
    bytes[5] === 0x61
  ) {
    return "image/gif";
  }

  if (
    bytes.length >= 12 &&
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50
  ) {
    return "image/webp";
  }

  if (
    bytes.length >= 2 &&
    bytes[0] === 0x42 &&
    bytes[1] === 0x4d
  ) {
    return "image/bmp";
  }

  const svgMarkup = tryDecodeSvg(bytes);

  if (svgMarkup && /<svg[\s>]/i.test(svgMarkup)) {
    return "image/svg+xml";
  }

  return null;
}

function isSupportedImageMime(mimeType: string): boolean {
  return (
    mimeType === "image/png" ||
    mimeType === "image/jpeg" ||
    mimeType === "image/gif" ||
    mimeType === "image/webp" ||
    mimeType === "image/bmp" ||
    mimeType === "image/svg+xml"
  );
}

function isImageBytes(bytes: Uint8Array, mimeType: string): boolean {
  return detectImageMime(bytes) === mimeType;
}

function tryDecodeSvg(bytes: Uint8Array): string | null {
  try {
    return new TextDecoder("utf-8").decode(bytes).trimStart();
  } catch {
    return null;
  }
}

function normalizeQuerySource(input: string): string {
  const trimmed = input.trim();

  if (trimmed === "") {
    return "";
  }

  const withoutQuestionMark = trimmed.startsWith("?") ? trimmed.slice(1) : trimmed;
  const hashIndex = withoutQuestionMark.indexOf("#");

  return hashIndex >= 0
    ? withoutQuestionMark.slice(0, hashIndex)
    : withoutQuestionMark;
}

function decodeQueryComponent(value: string): string {
  try {
    return decodeURIComponent(value.replaceAll("+", " "));
  } catch {
    return value.replaceAll("+", " ");
  }
}

function parseNameValuePair(
  source: string,
  separator: ":" | "="
): { name: string; value: string } {
  const separatorIndex = source.indexOf(separator);

  if (separatorIndex < 0) {
    return {
      name: source.trim(),
      value: ""
    };
  }

  return {
    name: source.slice(0, separatorIndex).trim(),
    value: source.slice(separatorIndex + 1).trim()
  };
}

function parseSetCookieLine(line: string): {
  name: string;
  value: string;
  attributes: Record<string, string | true>;
} {
  const normalized = line.replace(/^set-cookie\s*:/i, "").trim();
  const segments = normalized
    .split(";")
    .map((segment) => segment.trim())
    .filter((segment) => segment !== "");
  const [cookiePair = "", ...attributePairs] = segments;
  const cookie = parseNameValuePair(cookiePair, "=");
  const attributes: Record<string, string | true> = {};

  for (const attributePair of attributePairs) {
    const parsed = parseSetCookieAttribute(attributePair);
    attributes[parsed.name] = parsed.value;
  }

  return {
    name: cookie.name,
    value: cookie.value,
    attributes
  };
}

function parseSetCookieAttribute(source: string): {
  name: string;
  value: string | true;
} {
  const { name, value } = parseNameValuePair(source, "=");
  const normalizedName = normalizeSetCookieAttributeName(name);
  const hasExplicitValue = source.includes("=");

  return {
    name: normalizedName,
    value:
      !hasExplicitValue || (value === "" && isBooleanSetCookieAttribute(normalizedName))
        ? true
        : value
  };
}

function normalizeSetCookieAttributeName(name: string): string {
  const normalized = name.toLowerCase();

  switch (normalized) {
    case "expires":
      return "Expires";
    case "max-age":
      return "Max-Age";
    case "domain":
      return "Domain";
    case "path":
      return "Path";
    case "secure":
      return "Secure";
    case "httponly":
      return "HttpOnly";
    case "samesite":
      return "SameSite";
    default:
      return name.trim();
  }
}

function isBooleanSetCookieAttribute(name: string): boolean {
  return name === "Secure" || name === "HttpOnly";
}

function normalizeCronAlias(value: string): string {
  switch (value) {
    case "@yearly":
    case "@annually":
      return "0 0 1 1 *";
    case "@monthly":
      return "0 0 1 * *";
    case "@weekly":
      return "0 0 * * 0";
    case "@daily":
    case "@midnight":
      return "0 0 * * *";
    case "@hourly":
      return "0 * * * *";
    default:
      return value;
  }
}

function describeCronField(
  expression: string,
  spec: { min: number; max: number; label: string }
): CronFieldDescription {
  validateCronField(expression, spec);

  if (expression === "*") {
    return {
      expression,
      description: `every ${spec.label}`
    };
  }

  if (/^\*\/\d+$/.test(expression)) {
    return {
      expression,
      description: `every ${expression.slice(2)} ${spec.label}s`
    };
  }

  if (expression.includes(",")) {
    return {
      expression,
      description: `${spec.label}s ${expression}`
    };
  }

  if (expression.includes("/")) {
    const [base, step] = expression.split("/");
    return {
      expression,
      description: `${spec.label}s ${base} every ${step}`
    };
  }

  if (expression.includes("-")) {
    return {
      expression,
      description: `${spec.label}s ${expression}`
    };
  }

  return {
    expression,
    description: `${spec.label} ${expression}`
  };
}

function validateCronField(
  expression: string,
  spec: { min: number; max: number }
): void {
  if (expression === "*") {
    return;
  }

  for (const part of expression.split(",")) {
    validateCronSegment(part, spec);
  }
}

function validateCronSegment(
  segment: string,
  spec: { min: number; max: number }
): void {
  const [base, step] = segment.split("/");

  if (segment.includes("/") && (!step || !/^\d+$/.test(step) || Number(step) <= 0)) {
    throw new TransformError("INVALID_CRON_EXPRESSION");
  }

  if (base === "*") {
    return;
  }

  if (/^\d+$/.test(base)) {
    validateCronNumber(Number(base), spec);
    return;
  }

  const rangeMatch = base.match(/^(\d+)-(\d+)$/);

  if (!rangeMatch) {
    throw new TransformError("INVALID_CRON_EXPRESSION");
  }

  const rangeStart = Number(rangeMatch[1]);
  const rangeEnd = Number(rangeMatch[2]);
  validateCronNumber(rangeStart, spec);
  validateCronNumber(rangeEnd, spec);

  if (rangeStart > rangeEnd) {
    throw new TransformError("INVALID_CRON_EXPRESSION");
  }
}

function validateCronNumber(
  value: number,
  spec: { min: number; max: number }
): void {
  const normalizedValue = spec === cronFieldSpecs.dayOfWeek && value === 7 ? 0 : value;

  if (
    !Number.isInteger(normalizedValue) ||
    normalizedValue < spec.min ||
    normalizedValue > spec.max
  ) {
    throw new TransformError("INVALID_CRON_EXPRESSION");
  }
}

function buildCronSummary(
  minute: string,
  hour: string,
  dayOfMonth: string,
  month: string,
  dayOfWeek: string
): string {
  if (minute === "*" && hour === "*" && dayOfMonth === "*" && month === "*" && dayOfWeek === "*") {
    return "Runs every minute.";
  }

  return `Minute ${minute}, hour ${hour}, day-of-month ${dayOfMonth}, month ${month}, day-of-week ${dayOfWeek}.`;
}

function tokenizeShellCommand(input: string): string[] {
  const tokens: string[] = [];
  let current = "";
  let quote: "'" | '"' | null = null;

  for (let index = 0; index < input.length; index += 1) {
    const character = input[index];

    if (quote !== null) {
      if (character === quote) {
        quote = null;
        continue;
      }

      if (quote === '"' && character === "\\" && index + 1 < input.length) {
        current += input[index + 1];
        index += 1;
        continue;
      }

      current += character;
      continue;
    }

    if (character === "'" || character === '"') {
      quote = character;
      continue;
    }

    if (character === "\\" && index + 1 < input.length) {
      current += input[index + 1];
      index += 1;
      continue;
    }

    if (/\s/.test(character)) {
      if (current !== "") {
        tokens.push(current);
        current = "";
      }
      continue;
    }

    current += character;
  }

  if (quote !== null) {
    throw new TransformError("INVALID_CURL_COMMAND");
  }

  if (current !== "") {
    tokens.push(current);
  }

  return tokens;
}

function renderJsHeaders(headers: Record<string, string>): string {
  const objectBody = renderJsObject(headers, 0);

  if (!objectBody) {
    return "";
  }

  return `const headers = ${objectBody};`;
}

function renderJsObject(
  values: Record<string, string>,
  indentLevel: number
): string {
  const entries = Object.entries(values);

  if (entries.length === 0) {
    return "";
  }

  const indent = "  ".repeat(indentLevel);
  const childIndent = "  ".repeat(indentLevel + 1);
  return `{\n${entries
    .map(([key, value]) => `${childIndent}${JSON.stringify(key)}: ${JSON.stringify(value)}`)
    .join(",\n")}\n${indent}}`;
}

function renderFetchBody(command: ParsedCurlCommand): string {
  if (command.data === null) {
    return "";
  }

  if (isJsonBody(command)) {
    return `const body = JSON.stringify(${renderJsJsonValue(command.data)});`;
  }

  return `const body = ${JSON.stringify(command.data)};`;
}

function renderJsBodyValue(command: ParsedCurlCommand): string {
  if (command.data === null) {
    return "undefined";
  }

  return isJsonBody(command) ? renderJsJsonValue(command.data) : JSON.stringify(command.data);
}

function renderNodeBodyValue(command: ParsedCurlCommand): string {
  if (command.data === null) {
    return '""';
  }

  return isJsonBody(command)
    ? `JSON.stringify(${renderJsJsonValue(command.data)})`
    : JSON.stringify(command.data);
}

function renderPythonDict(values: Record<string, string>): string {
  const entries = Object.entries(values);

  if (entries.length === 0) {
    return "{}";
  }

  return `{\n${entries
    .map(([key, value]) => `    ${JSON.stringify(key)}: ${JSON.stringify(value)}`)
    .join(",\n")}\n}`;
}

function renderPythonBodyValue(command: ParsedCurlCommand): string {
  if (command.data === null) {
    return "None";
  }

  if (isJsonBody(command)) {
    return renderPythonJsonValue(command.data);
  }

  return JSON.stringify(command.data);
}

function isJsonBody(command: ParsedCurlCommand): boolean {
  const contentType = Object.entries(command.headers).find(([key]) => key.toLowerCase() === "content-type")?.[1];

  if (!contentType?.toLowerCase().includes("application/json") || command.data === null) {
    return false;
  }

  try {
    JSON.parse(command.data);
    return true;
  } catch {
    return false;
  }
}

function renderJsJsonValue(source: string): string {
  return JSON.stringify(JSON.parse(source), null, 2);
}

function renderPythonJsonValue(source: string): string {
  return JSON.stringify(JSON.parse(source), null, 4);
}

function buildLcsTable(leftLines: string[], rightLines: string[]): number[][] {
  const table = Array.from({ length: leftLines.length + 1 }, () =>
    Array.from({ length: rightLines.length + 1 }, () => 0)
  );

  for (let leftIndex = leftLines.length - 1; leftIndex >= 0; leftIndex -= 1) {
    for (let rightIndex = rightLines.length - 1; rightIndex >= 0; rightIndex -= 1) {
      table[leftIndex][rightIndex] =
        leftLines[leftIndex] === rightLines[rightIndex]
          ? table[leftIndex + 1][rightIndex + 1] + 1
          : Math.max(table[leftIndex + 1][rightIndex], table[leftIndex][rightIndex + 1]);
    }
  }

  return table;
}

function decodeHtmlEntityToken(token: string): string | null {
  if (token.startsWith("#x") || token.startsWith("#X")) {
    return decodeHtmlCodePoint(token.slice(2), 16);
  }

  if (token.startsWith("#")) {
    return decodeHtmlCodePoint(token.slice(1), 10);
  }

  return htmlEntityLookup.get(token) ?? htmlEntityLookup.get(token.toLowerCase()) ?? null;
}

function decodeHtmlCodePoint(value: string, radix: 10 | 16): string | null {
  if (value === "") {
    return null;
  }

  const codePoint = Number.parseInt(value, radix);

  if (!Number.isFinite(codePoint) || codePoint < 0 || codePoint > 0x10ffff) {
    return null;
  }

  try {
    return String.fromCodePoint(codePoint);
  } catch {
    return null;
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function dedupeStrings(values: string[]): string[] {
  const seen = new Set<string>();
  const output: string[] = [];

  for (const value of values) {
    if (value === "" || seen.has(value)) {
      continue;
    }

    seen.add(value);
    output.push(value);
  }

  return output;
}

function trimTrailingUrlPunctuation(value: string): string {
  return value.replace(/[),.;!?]+$/g, "");
}

function decodeJwtPart(part: string): Record<string, unknown> {
  const normalized = normalizeBase64Url(part);
  const bytes = decodeBase64ToBytes(normalized, "INVALID_JWT");

  try {
    const decoded = decoder.decode(bytes);
    const parsed = JSON.parse(decoded) as unknown;

    if (!isPlainObject(parsed)) {
      throw new TransformError("INVALID_JWT");
    }

    return parsed;
  } catch (error) {
    if (error instanceof TransformError) {
      throw error;
    }

    throw new TransformError("INVALID_JWT");
  }
}

function normalizeBase64Url(value: string): string {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padding = normalized.length % 4;

  if (padding === 0) {
    return normalized;
  }

  return normalized.padEnd(normalized.length + (4 - padding), "=");
}

function getJwtRegisteredClaims(
  payload: Record<string, unknown>
): Record<string, { value: number; iso: string }> {
  const claims: Record<string, { value: number; iso: string }> = {};

  for (const key of ["iat", "exp", "nbf"] as const) {
    const value = payload[key];

    if (typeof value !== "number" || !Number.isFinite(value)) {
      continue;
    }

    const timestamp = new Date(value * 1000);

    if (Number.isNaN(timestamp.getTime())) {
      continue;
    }

    claims[key] = {
      value,
      iso: timestamp.toISOString()
    };
  }

  return claims;
}

function formatTimestampInput(input: string, multiplier: number): string {
  const normalized = input.trim();

  if (normalized === "") {
    return "";
  }

  const value = Number(normalized);

  if (!Number.isFinite(value)) {
    throw new TransformError("INVALID_TIMESTAMP");
  }

  const date = new Date(value * multiplier);

  if (Number.isNaN(date.getTime())) {
    throw new TransformError("INVALID_TIMESTAMP");
  }

  return date.toISOString();
}

function parseDateInput(input: string): number {
  const normalized = input.trim();

  if (normalized === "") {
    throw new TransformError("INVALID_TIMESTAMP");
  }

  const timestamp = Date.parse(normalized);

  if (!Number.isFinite(timestamp)) {
    throw new TransformError("INVALID_TIMESTAMP");
  }

  return timestamp;
}
